<template lang="pug">
b-row
  b-col(cols="12")
    div.arr-wizard-form
      form-wizard.wizard-vertical.mb-3(
        color="#498ceb",
        layout="horizontal",
        :title="null",
        :subtitle="null",
        :finish-button-text="$t('message.submit')",
        :back-button-text="$t('message.previous')",
        :next-button-text="$t('message.next')",
        ref="arrFormRef"
      )
        // Custom footer
        template(v-slot:footer="slotProps")
          div(class="wizard-footer d-flex justify-content-between align-items-center")
            div
              b-button(
                variant="outline-secondary"
                @click="goToPreviousTab"
                :disabled="slotProps.activeTabIndex === 0"
              )
                | < &nbsp; {{ $t('message.previous') }}

              b-badge(
                v-if="isAutosaving"
                variant="light-info"
                class="ml-2"
              )
                span.spinner-border.spinner-border-sm.mr-1
                | Guardando borrador...

              b-badge(
                v-else-if="lastAutosaveTime"
                variant="light-success"
                class="ml-2"
              )
                | ✓ Guardado automático {{ formatAutosaveTime(lastAutosaveTime) }}

            div(class="ml-auto d-flex align-items-center")
              b-button(
                v-if="!slotProps.isLastStep"
                variant="primary"
                class="mr-2"
                @click="goToNextTab"
              )
                | {{ $t('message.next') }} &nbsp; >

              b-button(
                variant="success"
                @click="handleSubmit"
              )
                | {{ $t('message.submit') }}

        // PASO 1 - Selección de Tratamiento
        tab-content(:title="$t('Tratamiento')")
          validation-observer(ref="step1Tab", tag="form")
            b-row
              b-col(md="12")
                b-form-group(label="Seleccionar Tratamiento *", label-for="treatment")
                  validation-provider(name="Tratamiento", rules="required", v-slot="{ errors }")
                    v-select(
                      v-model="formData.treatment_id"
                      label="displayLabel"
                      :options="availableTreatmentsWithARI"
                      :reduce="item => item._id"
                      placeholder="Seleccione un tratamiento con ARI completo"
                      @input="handleTreatmentSelect"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-alert(v-if="noARIWarning", variant="warning", show)
              | Este tratamiento no tiene un ARI completo.
              | Debe completar el ARI antes de crear un ARR.
              | &nbsp;
              b-link(:to="{ name: 'privacy-inherent-risk-list' }")
                | Ir a Riesgo Inherente

            b-alert(v-if="existingARRWarning", variant="warning", show)
              | Este tratamiento ya tiene un ARR asociado.
              | &nbsp;
              b-link(:to="{ name: 'privacy-residual-risk-edit', params: { id: existingARRId } }")
                | Editar existente

            b-row(v-if="selectedTreatment")
              b-col(md="12")
                b-form-group(label="ID Tratamiento", label-for="treatmentId")
                  b-form-input(v-model="selectedTreatment.treatmentId", disabled, :style="{ width: '25%' }")

            b-row(v-if="selectedTreatment")
              b-col(md="12")
                b-form-group(label="Nombre", label-for="treatmentName")
                  b-form-input(v-model="selectedTreatment.nombre", disabled, :style="{ width: '75%' }")

            b-row(v-if="selectedInherentRisk")
              b-col(md="12")
                b-card(title="Riesgo Inherente (Auto-pull desde ARI)", border-variant="info")
                  b-row
                    b-col(md="3")
                      b-form-group(label="P (Probabilidad)")
                        b-badge(variant="light-primary", class="font-weight-bold p-2 w-100")
                          | {{ selectedInherentRisk.calculatedP || 'N/A' }}

                    b-col(md="3")
                      b-form-group(label="I (Impacto)")
                        b-badge(variant="light-info", class="font-weight-bold p-2 w-100")
                          | {{ selectedInherentRisk.calculatedI || 'N/A' }}

                    b-col(md="3")
                      b-form-group(label="Riesgo Inherente")
                        b-badge(variant="light-dark", class="font-weight-bold p-2 w-100")
                          | {{ selectedInherentRisk.inherentRisk || 'N/A' }}

                    b-col(md="3")
                      b-form-group(label="Nivel")
                        risk-level-badge(:level="selectedInherentRisk.inherentLevel || ''")

                  b-row
                    b-col(md="12")
                      b-link(:to="{ name: 'privacy-inherent-risk-edit', params: { id: selectedInherentRisk._id } }", target="_blank")
                        feather-icon(icon="ExternalLinkIcon", size="14", class="mr-25")
                        | Ver ARI completa

        // PASO 2 - Controles Preventivos (reducen Probabilidad)
        tab-content(:title="$t('Controles Preventivos')")
          validation-observer(ref="step2Tab", tag="form")
            b-alert(variant="info", show, class="mb-3")
              | <strong>Controles Preventivos</strong> reducen la Probabilidad (P) del riesgo.
              | Documente los controles y evalúe su efectividad (1-5).

            b-row
              b-col(md="12")
                b-form-group(label="Descripción del control preventivo *")
                  validation-provider(name="Control Preventivo", rules="required", v-slot="{ errors }")
                    b-form-textarea(
                      v-model="formData.preventiveControls"
                      placeholder="Describa los controles preventivos implementados para reducir la probabilidad del riesgo..."
                      rows="4"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row
              b-col(md="6")
                b-form-group(label="Efectividad del control preventivo *")
                  b-row.align-items-center
                    b-col(cols="11")
                      validation-provider(name="Efectividad Preventiva", rules="required", v-slot="{ errors }")
                        v-select(
                          v-model="formData.preventiveEffectiveness"
                          :options="effectivenessOptions"
                          placeholder="Seleccione nivel de efectividad"
                          label="label"
                          :reduce="opt => opt.value"
                        )
                          template(v-slot:option="option")
                            div
                              strong {{ option.label }}
                              br
                              small.text-muted {{ option.description }}
                        small(class="text-danger") {{ errors[0] }}
                    b-col(cols="1", class="pl-0")
                      b-button(
                        id="preventive-effectiveness-help"
                        variant="link"
                        size="sm"
                        class="p-0"
                      )
                        feather-icon(icon="HelpCircleIcon", size="20")

                b-popover(
                  target="preventive-effectiveness-help"
                  triggers="hover"
                  placement="right"
                )
                  | <strong>Niveles de Efectividad:</strong>
                  br
                  | <strong>1 - Nula:</strong> Sin efecto (multiplicador 1.00)
                  br
                  | <strong>2 - Baja:</strong> Efecto limitado (multiplicador 0.80)
                  br
                  | <strong>3 - Media:</strong> Efecto moderado (multiplicador 0.50)
                  br
                  | <strong>4 - Alta:</strong> Efecto significativo (multiplicador 0.20)
                  br
                  | <strong>5 - Muy Alta:</strong> Máxima eficacia (multiplicador 0.05)

        // PASO 3 - Controles Correctivos (reducen Impacto)
        tab-content(:title="$t('Controles Correctivos')")
          validation-observer(ref="step3Tab", tag="form")
            b-alert(variant="info", show, class="mb-3")
              | <strong>Controles Correctivos</strong> reducen el Impacto (I) del riesgo.
              | Documente los controles y evalúe su efectividad (1-5).

            b-row
              b-col(md="12")
                b-form-group(label="Descripción del control correctivo *")
                  validation-provider(name="Control Correctivo", rules="required", v-slot="{ errors }")
                    b-form-textarea(
                      v-model="formData.correctiveControls"
                      placeholder="Describa los controles correctivos implementados para reducir el impacto del riesgo..."
                      rows="4"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row
              b-col(md="6")
                b-form-group(label="Efectividad del control correctivo *")
                  b-row.align-items-center
                    b-col(cols="11")
                      validation-provider(name="Efectividad Correctiva", rules="required", v-slot="{ errors }")
                        v-select(
                          v-model="formData.correctiveEffectiveness"
                          :options="effectivenessOptions"
                          placeholder="Seleccione nivel de efectividad"
                          label="label"
                          :reduce="opt => opt.value"
                        )
                          template(v-slot:option="option")
                            div
                              strong {{ option.label }}
                              br
                              small.text-muted {{ option.description }}
                        small(class="text-danger") {{ errors[0] }}
                    b-col(cols="1", class="pl-0")
                      b-button(
                        id="corrective-effectiveness-help"
                        variant="link"
                        size="sm"
                        class="p-0"
                      )
                        feather-icon(icon="HelpCircleIcon", size="20")

                b-popover(
                  target="corrective-effectiveness-help"
                  triggers="hover"
                  placement="right"
                )
                  | <strong>Niveles de Efectividad:</strong>
                  br
                  | <strong>1 - Nula:</strong> Sin efecto (multiplicador 1.00)
                  br
                  | <strong>2 - Baja:</strong> Efecto limitado (multiplicador 0.80)
                  br
                  | <strong>3 - Media:</strong> Efecto moderado (multiplicador 0.50)
                  br
                  | <strong>4 - Alta:</strong> Efecto significativo (multiplicador 0.20)
                  br
                  | <strong>5 - Muy Alta:</strong> Máxima eficacia (multiplicador 0.05)

        // PASO 4 - Resultado Residual
        tab-content(:title="$t('Resultado Residual')")
          validation-observer(ref="step4Tab", tag="form")
            b-row
              b-col(md="12")
                b-card(title="Comparativa: Inherente vs Residual", border-variant="success")
                  b-row
                    b-col(md="6")
                      h6 Riesgo Inherente
                      b-table-simple(small, responsive)
                        tbody
                          tr
                            td <strong>P:</strong>
                            td
                              b-badge(variant="light-primary", class="font-weight-bold")
                                | {{ calculatedResults.P_inh || 'N/A' }}
                          tr
                            td <strong>I:</strong>
                            td
                              b-badge(variant="light-info", class="font-weight-bold")
                                | {{ calculatedResults.I_inh || 'N/A' }}
                          tr
                            td <strong>Riesgo:</strong>
                            td
                              b-badge(variant="light-dark", class="font-weight-bold")
                                | {{ calculatedResults.risk_inh || 'N/A' }}
                          tr
                            td <strong>Nivel:</strong>
                            td
                              risk-level-badge(:level="calculatedResults.level_inh || ''")

                    b-col(md="6")
                      h6 Riesgo Residual
                      b-table-simple(small, responsive)
                        tbody
                          tr
                            td <strong>P_res:</strong>
                            td
                              b-badge(variant="light-primary", class="font-weight-bold")
                                | {{ calculatedResults.P_res || 'N/A' }}
                          tr
                            td <strong>I_res:</strong>
                            td
                              b-badge(variant="light-info", class="font-weight-bold")
                                | {{ calculatedResults.I_res || 'N/A' }}
                          tr
                            td <strong>Riesgo:</strong>
                            td
                              b-badge(variant="light-dark", class="font-weight-bold")
                                | {{ calculatedResults.risk_res || 'N/A' }}
                          tr
                            td <strong>Nivel:</strong>
                            td
                              risk-level-badge(:level="calculatedResults.level_res || ''")

                  b-row(class="mt-3")
                    b-col(md="12")
                      b-alert(
                        :variant="reductionVariant"
                        show
                        class="text-center"
                      )
                        h5(class="mb-0")
                          feather-icon(:icon="reductionIcon", size="20")
                          | &nbsp; Reducción de Riesgo: {{ calculatedResults.reductionPct || 0 }}%

        // PASO 5 - Plan de Acción
        tab-content(:title="$t('Plan de Acción')")
          validation-observer(ref="step5Tab", tag="form")
            b-alert(variant="info", show, class="mb-3")
              | Defina el plan de acción para gestionar el riesgo residual.

            b-row
              b-col(md="6")
                b-form-group(label="Decisión de tratamiento del riesgo *")
                  validation-provider(name="Decisión", rules="required", v-slot="{ errors }")
                    v-select(
                      v-model="formData.decision"
                      :options="decisionOptions"
                      placeholder="Seleccione una decisión"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row
              b-col(md="12")
                b-form-group(label="Descripción del plan de acción *")
                  validation-provider(name="Plan de Acción", rules="required", v-slot="{ errors }")
                    b-form-textarea(
                      v-model="formData.actionPlan"
                      placeholder="Describa el plan de acción para gestionar el riesgo residual..."
                      rows="4"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row
              b-col(md="6")
                b-form-group(label="Responsable *")
                  validation-provider(name="Responsable", rules="required", v-slot="{ errors }")
                    v-select(
                      v-model="formData.responsible_id"
                      label="title"
                      :options="availableWorkers"
                      :reduce="worker => worker.value"
                      placeholder="Seleccione un responsable"
                    )
                    small(class="text-danger") {{ errors[0] }}

              b-col(md="6")
                b-form-group(label="Fecha objetivo *")
                  validation-provider(name="Fecha Objetivo", rules="required", v-slot="{ errors }")
                    b-form-datepicker(
                      v-model="formData.targetDate"
                      :min="new Date()"
                      locale="es"
                      placeholder="Seleccione una fecha"
                    )
                    small(class="text-danger") {{ errors[0] }}

            b-row
              b-col(md="6")
                b-form-group(label="Estado del plan *")
                  validation-provider(name="Estado", rules="required", v-slot="{ errors }")
                    v-select(
                      v-model="formData.status"
                      :options="statusOptions"
                      placeholder="Seleccione un estado"
                    )
                    small(class="text-danger") {{ errors[0] }}

    // Audit Log Table
    audit-log-table(
      v-if="isEdit && data"
      :history="data.history || []"
      :field-translations="{\
        treatment_id: $t('label.treatment'),\
        inherentAnalysis_id: $t('label.inherentAnalysis'),\
        inheritedP: $t('label.inheritedProbability'),\
        inheritedI: $t('label.inheritedImpact'),\
        inheritedRisk: $t('label.inheritedRisk'),\
        inheritedLevel: $t('label.inheritedLevel'),\
        preventiveControls: $t('label.preventiveControls'),\
        preventiveEffectiveness: $t('label.preventiveEffectiveness'),\
        correctiveControls: $t('label.correctiveControls'),\
        correctiveEffectiveness: $t('label.correctiveEffectiveness'),\
        residualP: $t('label.residualProbability'),\
        residualI: $t('label.residualImpact'),\
        residualRisk: $t('message.tableHeader.residualRisk'),\
        residualLevel: $t('label.residualLevel'),\
        riskReductionPct: $t('label.riskReduction'),\
        decision: $t('label.decision'),\
        actionPlan: $t('label.actionPlan'),\
        responsible_id: $t('label.responsible'),\
        targetDate: $t('label.targetDate'),\
        status: $t('message.tableHeader.status')\
      }"
    )
</template>

<script>
import { onMounted, watch, computed } from '@vue/composition-api'
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import RiskLevelBadge from '../../components/RiskLevelBadge.vue'
import AuditLogTable from '@/components/AuditLogTable.vue'
import useARRForm from './useARRForm'
import { BAlert, BBadge, BLink, BTableSimple } from 'bootstrap-vue'

export default {
  name: 'ARRForm',
  components: {
    FormWizard,
    TabContent,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    RiskLevelBadge,
    AuditLogTable,
    BAlert,
    BBadge,
    BLink,
    BTableSimple,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    onSaveDraft: {
      type: Function,
      default: null,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    currentArrId: {
      type: String,
      default: null,
    },
  },
  setup(props, context) {
    const {
      arrFormRef,
      availableTreatmentsWithARI,
      availableWorkers,
      selectedTreatment,
      selectedInherentRisk,
      noARIWarning,
      existingARRWarning,
      existingARRId,
      isAutosaving,
      lastAutosaveTime,
      formData,
      calculatedResults,
      effectivenessOptions,
      decisionOptions,
      statusOptions,
      step1Tab,
      step2Tab,
      step3Tab,
      step4Tab,
      step5Tab,
      fetchTreatmentsWithARI,
      fetchWorkers,
      handleTreatmentSelect,
      handleSubmit,
      goToNextTab,
      goToPreviousTab,
      formatAutosaveTime,
    } = useARRForm(props, context)

    const reductionVariant = computed(() => {
      const pct = calculatedResults.reductionPct || 0
      if (pct >= 50) return 'success'
      if (pct >= 25) return 'warning'
      if (pct > 0) return 'info'
      return 'secondary'
    })

    const reductionIcon = computed(() => {
      const pct = calculatedResults.reductionPct || 0
      if (pct >= 50) return 'TrendingDownIcon'
      if (pct > 0) return 'ArrowDownIcon'
      return 'MinusIcon'
    })

    onMounted(async () => {
      await Promise.all([
        fetchTreatmentsWithARI(),
        fetchWorkers(),
      ])
    })

    // Watch for data changes (for edit mode)
    watch(() => props.data, (newData) => {
      if (newData && Object.keys(newData).length > 0) {
        Object.assign(formData, newData)

        if (newData.treatment_id) {
          // Only call handleTreatmentSelect if treatments are loaded
          if (availableTreatmentsWithARI.value.length > 0) {
            handleTreatmentSelect(newData.treatment_id)
          } else {
            // Wait for treatments to load, then call handleTreatmentSelect
            const unwatch = watch(availableTreatmentsWithARI, (treatments) => {
              if (treatments.length > 0) {
                handleTreatmentSelect(newData.treatment_id)
                unwatch() // Stop watching after first call
              }
            })
          }
        }
      }
    }, { immediate: true, deep: true })

    return {
      arrFormRef,
      availableTreatmentsWithARI,
      availableWorkers,
      selectedTreatment,
      selectedInherentRisk,
      noARIWarning,
      existingARRWarning,
      existingARRId,
      isAutosaving,
      lastAutosaveTime,
      formData,
      calculatedResults,
      effectivenessOptions,
      decisionOptions,
      statusOptions,
      reductionVariant,
      reductionIcon,
      step1Tab,
      step2Tab,
      step3Tab,
      step4Tab,
      step5Tab,
      handleTreatmentSelect,
      handleSubmit,
      goToNextTab,
      goToPreviousTab,
      formatAutosaveTime,
    }
  },
}
</script>

<style lang="scss" scoped>
.wizard-footer {
  padding: 1rem 0;
  border-top: 1px solid #e0e0e0;
  margin-top: 1rem;
}

::v-deep .form-control {
  background-color: #e9ecef !important;
}

::v-deep .form-control:disabled {
  background-color: #c6c6c6 !important;
}

::v-deep textarea.form-control {
  background-color: #e9ecef !important;
}

::v-deep textarea.form-control:disabled {
  background-color: #c6c6c6 !important;
}

::v-deep input.form-control {
  background-color: #e9ecef !important;
}

::v-deep input.form-control:disabled {
  background-color: #c6c6c6 !important;
}

::v-deep .vs__dropdown-toggle {
  background-color: #e9ecef !important;
}

::v-deep .v-select.vs--disabled .vs__dropdown-toggle {
  background-color: #c6c6c6 !important;
}
</style>

<style lang="scss">
.arr-wizard-form {
  .v-select .vs__dropdown-toggle {
    background-color: #e9ecef !important;
  }

  .v-select.vs--disabled .vs__dropdown-toggle {
    background-color: #c6c6c6 !important;
    opacity: 1 !important;
  }

  input.form-control[disabled],
  input.form-control:disabled {
    background-color: #c6c6c6 !important;
    opacity: 1 !important;
  }

  textarea.form-control[disabled],
  textarea.form-control:disabled {
    background-color: #c6c6c6 !important;
    opacity: 1 !important;
  }

  .form-control[disabled],
  .form-control:disabled {
    background-color: #c6c6c6 !important;
    opacity: 1 !important;
  }
}
</style>
